import { calculateDarcyFrictionFactor } from "./pipeFlow";

export interface PumpSizingInputs {
  density: number;
  viscosity: number;
  gravity: number;
  vaporPressure: number;
  flow: number;
  suctionGaugePressure: number;
  dischargeGaugePressure: number;
  suctionStaticHead: number;
  dischargeStaticHead: number;
  suctionPipeLength: number;
  suctionOuterDiameter: number;
  suctionWallThickness: number;
  suctionRoughness: number;
  suctionLossCoefficient: number;
  suctionKv: number;
  suctionKvCount: number;
  dischargePipeLength: number;
  dischargeOuterDiameter: number;
  dischargeWallThickness: number;
  dischargeRoughness: number;
  dischargeLossCoefficient: number;
  dischargeKv: number;
  dischargeKvCount: number;
  requiredNpsh: number;
}

export interface PumpPipeResult {
  innerDiameter: number;
  area: number;
  velocity: number;
  reynoldsNumber: number;
  frictionFactor: number;
  localPressureLoss: number;
  frictionPressureLoss: number;
  totalPressureLoss: number;
}

export interface PumpSizingResult {
  requiredPressureIncrease: number;
  requiredHead: number;
  availableNpsh: number;
  requiredNpsh: number;
  npshMargin: number;
  suction: PumpPipeResult;
  discharge: PumpPipeResult;
}

const ATMOSPHERIC_PRESSURE = 101325;
const calculatePipe = (
  density: number,
  viscosity: number,
  flow: number,
  length: number,
  outerDiameter: number,
  wallThickness: number,
  roughness: number,
  lossCoefficient: number,
  kv: number,
  kvCount: number
): PumpPipeResult | null => {
  const innerDiameter = outerDiameter - 2 * wallThickness;
  if (innerDiameter <= 0) return null;

  const area = (Math.PI * innerDiameter * innerDiameter) / 4;
  const velocity = flow / area;
  const reynoldsNumber =
    flow === 0 ? 0 : (density * velocity * innerDiameter) / viscosity;
  const frictionFactor = calculateDarcyFrictionFactor(
    reynoldsNumber,
    roughness,
    innerDiameter
  );
  const velocityPressure = (density * velocity * velocity) / 2;
  const kvPressureLoss =
    kvCount === 0 ? 0 : kvCount * (density / 1000) * Math.pow(flow / kv, 2) * 1e5;
  const localPressureLoss =
    velocityPressure * lossCoefficient + kvPressureLoss;
  const frictionPressureLoss =
    velocityPressure * frictionFactor * (length / innerDiameter);

  return {
    innerDiameter,
    area,
    velocity,
    reynoldsNumber,
    frictionFactor,
    localPressureLoss,
    frictionPressureLoss,
    totalPressureLoss: localPressureLoss + frictionPressureLoss,
  };
};

export const calculatePumpSizing = (
  input: PumpSizingInputs
): PumpSizingResult | null => {
  const values = Object.values(input);
  if (values.some((value) => !Number.isFinite(value))) return null;

  const positiveValues = [
    input.density,
    input.viscosity,
    input.gravity,
    input.suctionOuterDiameter,
    input.dischargeOuterDiameter,
  ];
  const nonNegativeValues = [
    input.vaporPressure,
    input.flow,
    input.suctionPipeLength,
    input.suctionWallThickness,
    input.suctionRoughness,
    input.suctionLossCoefficient,
    input.suctionKv,
    input.suctionKvCount,
    input.dischargePipeLength,
    input.dischargeWallThickness,
    input.dischargeRoughness,
    input.dischargeLossCoefficient,
    input.dischargeKv,
    input.dischargeKvCount,
    input.requiredNpsh,
  ];

  if (
    positiveValues.some((value) => value <= 0) ||
    nonNegativeValues.some((value) => value < 0) ||
    (input.suctionKvCount > 0 && input.suctionKv <= 0) ||
    (input.dischargeKvCount > 0 && input.dischargeKv <= 0)
  ) {
    return null;
  }

  const suction = calculatePipe(
    input.density,
    input.viscosity,
    input.flow,
    input.suctionPipeLength,
    input.suctionOuterDiameter,
    input.suctionWallThickness,
    input.suctionRoughness,
    input.suctionLossCoefficient,
    input.suctionKv,
    input.suctionKvCount
  );
  const discharge = calculatePipe(
    input.density,
    input.viscosity,
    input.flow,
    input.dischargePipeLength,
    input.dischargeOuterDiameter,
    input.dischargeWallThickness,
    input.dischargeRoughness,
    input.dischargeLossCoefficient,
    input.dischargeKv,
    input.dischargeKvCount
  );

  if (!suction || !discharge) return null;

  const staticPressureDifference =
    input.dischargeGaugePressure - input.suctionGaugePressure;
  const staticElevationDifference =
    input.density *
    input.gravity *
    (input.dischargeStaticHead - input.suctionStaticHead);
  const requiredPressureIncrease =
    staticPressureDifference +
    staticElevationDifference +
    suction.totalPressureLoss +
    discharge.totalPressureLoss;
  const requiredHead =
    requiredPressureIncrease / (input.density * input.gravity);
  const availableNpsh =
    (input.suctionGaugePressure +
      ATMOSPHERIC_PRESSURE -
      input.vaporPressure) /
      (input.density * input.gravity) +
    input.suctionStaticHead -
    suction.totalPressureLoss / (input.density * input.gravity);
  const npshMargin = availableNpsh - input.requiredNpsh;

  return {
    requiredPressureIncrease,
    requiredHead,
    availableNpsh,
    requiredNpsh: input.requiredNpsh,
    npshMargin,
    suction,
    discharge,
  };
};
